import { VendorFilters } from '@/pages/vendors/VendorFilters'
import { VendorList } from '@/pages/vendors/VendorList'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createFileRoute,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router'
import AddVendorDialog from '@/pages/vendors/AddVendorDialog'
import { ImportVendorsDialog } from '@/pages/vendors/ImportVendorsDialog'
import VendorDiscoveryPanel from '@/pages/vendors/VendorDiscoveryPanel'
import { FilterPill } from '@/components/ui/filter-pill'
import {
  CATEGORY_LABELS,
  RiskTierFilter,
  StatusFilter,
  CategoryFilter,
} from '@/pages/vendors/type'
import { useSetAtom } from 'jotai'
import {
  Plus,
  Sparkles,
  Building2,
  ChevronDown,
  FileDown,
  MoreHorizontal,
  Download,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { formatEnrolledFrameworksLabel } from '@/lib/compliance-framework'
import { paginationSearchSchema } from '@/lib/pagination'
import { trpc } from '@/lib/trpc'
import { downloadBase64File } from '@/utils/download'
import {
  useEnabledFrameworks,
  useHipaaEnabled,
} from '@/hooks/useComplianceFramework'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useUrlPagination } from '@/hooks/useUrlPagination'
import { Button } from '@deepguide-ai/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { VendorOverview } from '@/pages/vendors/VendorOverview'
import {
  PageEmptyState,
  pageEmptyStateOutlineButtonClass,
  pageEmptyStatePrimaryButtonClass,
} from '@deepguide-ai/ui/page-empty-state'
import { TableEmptyState } from '@deepguide-ai/ui/table-empty-state'
import { TableSkeleton } from '@deepguide-ai/ui/table-skeleton'
import { useGenerateReport } from '@/components/compliance/GenerateReportButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import ScreenataPageHeader from '@/components/compliance/ScreenataPageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'

type BaaFilter = 'all' | 'ephi_only' | 'REQUIRED' | 'SIGNED' | 'EXPIRED'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/vendors'
)({
  component: VendorManagementPage,
  validateSearch: paginationSearchSchema,
  staticData: { pageHeader: { title: 'Vendors', pad: false } },
})

function VendorManagementPage() {
  const enrolledFrameworks = useEnabledFrameworks()
  const enrolledLabel = formatEnrolledFrameworksLabel(enrolledFrameworks)
  const hipaaEnabled = useHipaaEnabled()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { workspaceId } = useParams({ strict: false }) as {
    workspaceId: string
  }
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  const [riskTierFilter, setRiskTierFilter] = useState<RiskTierFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all')
  const [baaFilter, setBaaFilter] = useState<BaaFilter>('all')
  const [showAddForm, setShowAddForm] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const search = useSearch({
    from: '/(protected)/workspaces/$workspaceId/vendors',
  })
  const { page, pageSize, setPage, setPageSize } = useUrlPagination(search, [
    riskTierFilter,
    statusFilter,
    categoryFilter,
    baaFilter,
  ])

  // Queries
  const statsQuery = useQuery(trpc.vendorManagement.getStats.queryOptions())
  const distributionQuery = useQuery(trpc.vendorManagement.getDistribution.queryOptions())
  const baaStatsQuery = useQuery({
    ...trpc.vendorManagement.getBaaStats.queryOptions(),
    enabled: hipaaEnabled,
  })
  const listQuery = useQuery({
    ...trpc.vendorManagement.list.queryOptions({
      page,
      pageSize,
      includeOffboarded: true,
      ...(riskTierFilter !== 'all' ? { riskTier: riskTierFilter } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      ...(categoryFilter !== 'all' ? { category: categoryFilter } : {}),
      // HIPAA BAA filter — gates on hipaaEnabled so SOC 2-only workspaces
      // never send these params.
      ...(hipaaEnabled && baaFilter === 'ephi_only'
        ? { containsEphi: true }
        : {}),
      ...(hipaaEnabled && baaFilter !== 'all' && baaFilter !== 'ephi_only'
        ? { baaStatus: baaFilter }
        : {}),
    }),
    placeholderData: (previousData) => previousData,
  })
  const rolesQuery = useQuery(trpc.complianceRole.list.queryOptions())

  // Mutations
  const seedMutation = useMutation(
    trpc.vendorManagement.seedDefaults.mutationOptions()
  )
  const bulkUpdateMutation = useMutation(
    trpc.vendorManagement.bulkUpdate.mutationOptions()
  )
  const setTierMutation = useMutation(
    trpc.vendorManagement.setTier.mutationOptions()
  )
  const setResidualTierMutation = useMutation(
    trpc.vendorManagement.setResidualTier.mutationOptions()
  )
  const exportMutation = useMutation({
    ...trpc.vendorManagement.exportVendorList.mutationOptions(),
    onSuccess: (data) => {
      downloadBase64File(data.data, data.filename, data.contentType)
      toast.success('Vendor list exported')
    },
    onError: () => toast.error('Failed to export vendor list'),
  })
  // Shared with the standalone GenerateReportButton — same toasts and gate.
  const reportAction = useGenerateReport('VENDOR_INVENTORY', workspaceId)
  const bulkInitiateAssessmentMutation = useMutation(
    trpc.vendorAssessment.bulkInitiate.mutationOptions()
  )

  const stats = statsQuery.data
  const pageItems = listQuery.data?.items ?? []
  const totalItems = listQuery.data?.total ?? 0
  const isListInitialLoading = listQuery.isLoading && !listQuery.data
  const isEmpty =
    totalItems === 0 &&
    riskTierFilter === 'all' &&
    statusFilter === 'all' &&
    categoryFilter === 'all' &&
    baaFilter === 'all'

  useEffect(() => {
    if (!canWorkspaceWrite && selectedIds.size > 0) {
      setSelectedIds(new Set())
    }
  }, [canWorkspaceWrite, selectedIds.size])

  const invalidateAll = () => {
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.listOptions.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vendorManagement.getStats.queryKey(),
    })
    queryClient.invalidateQueries({ queryKey: trpc.vendorManagement.getDistribution.queryKey() })
    if (hipaaEnabled) {
      queryClient.invalidateQueries({
        queryKey: trpc.vendorManagement.getBaaStats.queryKey(),
      })
    }
  }

  const handleBulkUpdate = async (data: Record<string, unknown>) => {
    if (!canWorkspaceWrite) return
    if (selectedIds.size === 0) return
    await bulkUpdateMutation.mutateAsync({
      ids: [...selectedIds],
      data: data as any,
    })
    invalidateAll()
  }

  const handleBulkTierUpdate = async (tier: string) => {
    if (!canWorkspaceWrite) return
    if (selectedIds.size === 0) return
    await Promise.all(
      [...selectedIds].map((id) =>
        setTierMutation.mutateAsync({
          id,
          tier: tier as any,
          rationale: 'Set manually from vendor bulk action.',
        }),
      ),
    )
    invalidateAll()
  }

  const handleBulkResidualTierUpdate = async (tier: string | null) => {
    if (!canWorkspaceWrite) return
    if (selectedIds.size === 0) return
    await Promise.all(
      [...selectedIds].map((id) =>
        setResidualTierMutation.mutateAsync({
          id,
          tier: tier as any,
          rationale: tier ? 'Set manually from vendor bulk action.' : undefined,
        }),
      ),
    )
    invalidateAll()
  }

  const handleBulkStartAssessment = async () => {
    if (!canWorkspaceWrite) return
    if (selectedIds.size === 0) return
    await bulkInitiateAssessmentMutation.mutateAsync({
      vendorIds: [...selectedIds],
    })
    invalidateAll()
  }

  const toggleSelectAll = () => {
    if (!canWorkspaceWrite) return
    const pageIds = pageItems.map((i: any) => i.id)
    const allOnPageSelected =
      pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id))
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allOnPageSelected) {
        for (const id of pageIds) next.delete(id)
      } else {
        for (const id of pageIds) next.add(id)
      }
      return next
    })
  }

  const toggleSelect = (id: string) => {
    if (!canWorkspaceWrite) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleSeedDefaults = async () => {
    if (!canWorkspaceWrite) return
    try {
      const { created } = await seedMutation.mutateAsync()
      invalidateAll()
      toast.success(
        created > 0
          ? `Added ${created} vendor${created === 1 ? '' : 's'} from context`
          : 'No new vendors to add from context',
      )
    } catch {
      toast.error('Failed to seed vendors from context')
    }
  }

  const openVendorDetail = (vendorId: string, detailTab?: string) => {
    navigate({
      to: '/workspaces/$workspaceId/vendors/$vendorId',
      params: { workspaceId, vendorId },
      ...(detailTab ? { search: { tab: detailTab } } : {}),
    })
  }

  // Page context for workspace chat
  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    setPageContext({
      page: 'vendors',
      summary: [
          `Vendor Inventory: ${stats?.total ?? 0} vendors.`,
          `${stats?.highRisk ?? 0} high risk, ${stats?.active ?? 0} active, ${stats?.needsReview ?? 0} needing review, ${stats?.assessmentsOverSla ?? 0} assessments over SLA.`,
        `Showing ${totalItems} items with current filters.`,
      ]
        .filter(Boolean)
        .join(' '),
      data: stats ?? {},
    })
    return () => setPageContext(null)
  }, [stats, totalItems, setPageContext])

  return (
    <div className='flex flex-col gap-4 px-6 py-6'>
      <PageHeaderActions>
        {/* Occasional actions live in the … overflow; Add vendor is the one
            visible action, a dropdown over its three entry paths. */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant='outline'
              size='sm'
              className='h-8 rounded-sm border-border-divider px-2 text-[12px]'
              aria-label='More actions'
            >
              <MoreHorizontal className='h-4 w-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            {!reportAction.hidden && (
              <DropdownMenuItem
                disabled={reportAction.isPending}
                onSelect={() => reportAction.generate()}
              >
                <FileDown className='h-3.5 w-3.5' />
                {reportAction.isPending ? 'Generating...' : 'Generate Report'}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              disabled={exportMutation.isPending}
              onSelect={() => exportMutation.mutate()}
            >
              <Download className='h-3.5 w-3.5' />
              {exportMutation.isPending ? 'Exporting...' : 'Export'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {canWorkspaceWrite && (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                size='sm'
                className='h-8 rounded-sm bg-primary px-3 text-[12px] text-primary-foreground hover:bg-primary/90'
              >
                <Plus className='h-3.5 w-3.5' />
                Add Vendor
                <ChevronDown className='h-3.5 w-3.5' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem onSelect={() => setShowAddForm(true)}>
                <Plus className='h-3.5 w-3.5' />
                Add a single vendor
              </DropdownMenuItem>
              {/* #1430 的批量导入与现有 Add Vendor 弹窗相互独立。 */}
              <DropdownMenuItem onSelect={() => setShowImportDialog(true)}>
                <Upload className='h-3.5 w-3.5' />
                Import / update CSV
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={seedMutation.isPending}
                onSelect={() => handleSeedDefaults()}
              >
                <Sparkles
                  className={`h-3.5 w-3.5 ${seedMutation.isPending ? 'animate-spin' : ''}`}
                />
                {seedMutation.isPending ? 'Seeding...' : 'Seed from context'}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </PageHeaderActions>
      {/* Page title */}
      <ScreenataPageHeader
        title='Vendor Inventory'
        description={`Track third-party vendors, risk tiers, review schedules, and lifecycle status for ${enrolledLabel} audits.`}
      />
      <VendorOverview
        data={distributionQuery.data}
        loading={distributionQuery.isPending}
        reviewDue={stats?.needsReview}
        overSla={stats?.assessmentsOverSla}
        lowRenewalMissing={stats?.lowRenewalMissing}
        baaStats={hipaaEnabled ? baaStatsQuery.data : undefined}
        onBaaClick={() => setBaaFilter('ephi_only')}
      />
      {/* Discovered-vendor review panel (#971 M2) */}
      <VendorDiscoveryPanel readOnly={!canWorkspaceWrite} />
      {/* #990 — vendor analytics, shown once there's inventory to chart */}
      {/* Filter Row */}
      <div className='flex flex-wrap items-center gap-3'>
        <VendorFilters
          includeOffboarded
          riskTier={riskTierFilter}
          status={statusFilter}
          onRiskTierChange={setRiskTierFilter}
          onStatusChange={setStatusFilter}
        />

        <Select
          value={categoryFilter}
          onValueChange={(v) => setCategoryFilter(v as CategoryFilter)}
        >
          <SelectTrigger className='font-body h-8 w-[140px] rounded-sm border border-border-divider bg-card px-3 text-[12px] font-medium text-muted-foreground'>
            <SelectValue placeholder='Category' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All Categories</SelectItem>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hipaaEnabled && (
          <>
            <div className='mx-1 h-5 w-px bg-border-soft' />
            <span className='font-body text-[12px] text-muted-foreground'>BAA:</span>
            {(
              [
                { value: 'all', label: 'All' },
                { value: 'ephi_only', label: 'Business Associates' },
                {
                  value: 'REQUIRED',
                  label: 'Needed',
                  color: 'bg-status-red text-white',
                },
                {
                  value: 'SIGNED',
                  label: 'Signed',
                  color: 'bg-status-green text-white',
                },
                {
                  value: 'EXPIRED',
                  label: 'Expired',
                  color: 'bg-status-orange text-white',
                },
              ] as const
            ).map((t) => (
              <FilterPill
                key={t.value}
                active={baaFilter === t.value}
                onClick={() => setBaaFilter(t.value)}
                lightActiveClassName={'color' in t ? t.color : undefined}
              >
                {t.label}
              </FilterPill>
            ))}
          </>
        )}
      </div>
      {/* Content */}
      {isListInitialLoading  ? (
        <TableSkeleton
          rows={6}
          columns={hipaaEnabled ? 15 : 14}
          columnWidths={[
            16,
            52,
            '22%',
            118,
            84,
            66,
            66,
            92,
            72,
            ...(hipaaEnabled ? [72] : []),
            92,
            82,
            82,
            28,
            52,
          ]}
        />
      ) : isEmpty ? (
        <PageEmptyState
          icon={Building2}
          title='No vendors in inventory yet'
          description='Seed your vendor inventory from company context collected during setup, or add vendors manually.'
          footnote='Seeded vendors are pre-classified based on your company context. You are responsible for reviewing and confirming accuracy before audit.'
          actions={
            canWorkspaceWrite ? (
              <>
              <Button
                variant='outline'
                size='sm'
                className={pageEmptyStateOutlineButtonClass}
                onClick={handleSeedDefaults}
                disabled={seedMutation.isPending}
              >
                <Sparkles className='h-3.5 w-3.5' />
                {seedMutation.isPending
                  ? 'Seeding...'
                  : 'Seed from Company Context'}
              </Button>
              <Button
                size='sm'
                className={pageEmptyStatePrimaryButtonClass}
                onClick={() => setShowAddForm(true)}
              >
                <Plus className='h-3.5 w-3.5' />
                Add Vendor Manually
              </Button>
              </>
            ) : null
          }
        />
      ) : totalItems === 0 ? (
        <TableEmptyState
          message='No vendors match the selected filters.'
          className='min-h-[338.5px]'
        />
      ) : (
          <VendorList
            items={pageItems}
            hipaaEnabled={hipaaEnabled}
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onOpenVendor={openVendorDetail}
            onOpenRisks={(id) => openVendorDetail(id, 'risks')}
            management={canWorkspaceWrite ? {
              selectedIds, updating: bulkUpdateMutation.isPending,
              starting: bulkInitiateAssessmentMutation.isPending,
              roles: rolesQuery.data ?? [],
              clearSelection: () => setSelectedIds(new Set()),
              toggleSelectAll, toggleSelect, handleBulkTierUpdate,
              handleBulkResidualTierUpdate, handleBulkUpdate, handleBulkStartAssessment,
            } : undefined}
          />
      )}
      {/* Add Vendor Dialog */}
      {showAddForm && canWorkspaceWrite && (
        <AddVendorDialog
          open={showAddForm}
          onClose={() => setShowAddForm(false)}
          onCreated={invalidateAll}
        />
      )}
      {/* 在弹窗挂载点再次检查权限，避免只读用户打开写入界面。 */}
      {showImportDialog && canWorkspaceWrite && (
        <ImportVendorsDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          readOnly={!canWorkspaceWrite}
        />
      )}
    </div>
  )
}
